import { Button } from '@studio/design-system/button';
import type { KeyboardEvent, ReactElement, ReactNode } from 'react';
import { useCallback, useState } from 'react';
import { onboardingContextFor, useOnboardingTracker } from '../hooks/use-onboarding-tracker.js';
import { Sel, STagNew } from '#~/stag/tag.js';
import { FunnelShell } from '../components/funnel-shell.js';
import { useAutoFocus } from '../hooks/use-auto-focus.js';
import { useStepView } from '../hooks/use-step-view.js';

// Choose-method screen per prototype Step 2. Two-step interaction:
// click a card to select it (highlight), then click Continue to proceed.
// Per rule: ui-architecture.md — cards are real <button> elements.

type MethodChoice = 'auto' | 'manual' | null;

interface ScreenChooseMethodProps {
  readonly onPickDiscovery: () => void;
  readonly onPickManual: () => void;
  readonly onBack: () => void;
  /** Escape / the close affordance: dismiss WITHOUT recording the permanent
   *  skip. Distinct from onSkip, which is an explicit user decision to defer —
   *  see gate.tsx handleDismiss (ENG-3796 live-run defect 1). */
  readonly onDismiss: () => void;
}

export const ScreenChooseMethod = ({
  onPickDiscovery,
  onPickManual,
  onBack,
  onDismiss
}: ScreenChooseMethodProps): ReactElement => {
  useStepView('choose-method');

  // Pre-select the recommended option so Continue is enabled on mount
  const [pick, setPick] = useState<MethodChoice>('auto');
  const primaryRef = useAutoFocus<HTMLButtonElement>();

  const tracker = useOnboardingTracker();

  const handleContinue = useCallback((): void => {
    if (pick === null) return;
    // Routed through buildOnboardingContext like every other onboarding emit.
    // Before this the fork events were the ONLY ones sent as a bare property bag,
    // so they arrived without `category` or `funnel_stage` — and funnel_stage is
    // the single dimension the electron/standalone-web funnels are built on
    // (telemetry.ts header). A PM filtering the funnel on that dimension lost the
    // fork step entirely, which is the one step that says which path the user
    // took. `method` is preserved verbatim via `extra` so existing dashboards
    // keyed on it keep working. Per rule: reference-rules-in-comments
    // (ai.knowledge/global).
    // ENG-4305's `onboarding_step_submitted` with the add-method dimension, not
    // an event of its own: "the user committed a choice on this step" is
    // exactly what that event means, and the fork is a step like any other.
    // `method` is preserved verbatim so dashboards keyed on it keep working.
    tracker.trackStepSubmitted(onboardingContextFor('choose-method'), {
      method: pick === 'auto' ? 'discovery' : 'manual'
    });
    if (pick === 'auto') onPickDiscovery();
    else onPickManual();
  }, [pick, tracker, onPickDiscovery, onPickManual]);

  return (
    <FunnelShell
      announceTitle='How would you like to connect?'
      onEscape={onDismiss}
      progressStep={2}
    >
      <h1 className='text-2xl font-demibold text-white'>How would you like to connect?</h1>
      <p className='mt-12 max-w-[28rem] text-md text-foreground/75'>
        We work with most IP cameras that support RTSP and VISCA-over-IP.
      </p>

      <div className='mt-32 grid w-full max-w-[40rem] grid-cols-2 gap-12'>
        <MethodCard
          focusRef={primaryRef}
          icon={<NetworkScanIcon />}
          selected={pick === 'auto'}
          stagSubject={Sel.OnboardingChooseMethod}
          stagAction='pick_discovery'
          title='Find cameras on my network'
          description="We'll scan your local network for compatible cameras."
          recommended
          onClick={() => setPick('auto')}
        />
        <MethodCard
          icon={<KeyboardIcon />}
          selected={pick === 'manual'}
          stagSubject={Sel.OnboardingChooseMethod}
          stagAction='pick_manual'
          title='Enter camera details manually'
          description='IP address, RTSP URL, and VISCA port.'
          onClick={() => setPick('manual')}
        />
      </div>

      {/* Footer — Back text link on left, Continue on right per prototype */}
      <div className='mt-32 flex w-full max-w-[40rem] items-center justify-between'>
        <button
          {...STagNew(Sel.OnboardingChooseMethod, 'back')}
          className='text-sm text-secondary underline-offset-4 transition hover:text-foreground hover:underline'
          onClick={onBack}
          type='button'
        >
          Back
        </button>
        <Button
          {...STagNew(Sel.OnboardingChooseMethod, 'continue')}
          disabled={pick === null}
          onClick={handleContinue}
          size='md'
          variant='primary'
        >
          Continue
        </Button>
      </div>
    </FunnelShell>
  );
};

interface MethodCardProps {
  readonly stagSubject: Sel;
  readonly stagAction: string;
  readonly title: string;
  readonly description: ReactNode;
  readonly icon?: ReactNode;
  readonly selected?: boolean;
  readonly recommended?: boolean;
  readonly onClick: () => void;
  // `focusRef` instead of `ref` so React 18 does not warn about
  // forwarding refs to a function component. The parent owns the ref
  // and we just attach it to the underlying <button>. Per
  // ui-architecture.md keyboard focus is a hard requirement on this
  // primitive, so a stable ref attachment matters.
  readonly focusRef?: React.Ref<HTMLButtonElement>;
}

// Internal card primitive. Native <button> so Enter/Space activate it
// and screen readers announce it as actionable. We use a real <button>
// instead of design-system Button because Button's variants don't
// include "card with title + body" — duplicating that shape inside a
// Button would fight its layout. Per ui-architecture.md the only hard
// requirement is keyboard reachability + a focus ring, both satisfied
// by the focus-visible:ring classes below.
const MethodCard = ({
  stagSubject,
  stagAction,
  title,
  description,
  icon,
  selected = false,
  recommended = false,
  onClick,
  focusRef
}: MethodCardProps): ReactElement => {
  // Space and Enter natively activate <button>, but we still mark the
  // handler explicitly so a future refactor that swaps to <div role="button">
  // does not silently lose keyboard activation (think-in-systems.md).
  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <button
      ref={focusRef}
      className={`focus-visible:ring-primary/50 group relative flex w-full flex-col items-start gap-4 rounded-12 border px-16 py-16 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 ${
        selected
          ? 'border-primary bg-base-200 shadow-[0_0_0_1px_rgb(var(--primary))]'
          : 'border-base-100 bg-base-100 hover:border-base-50 hover:bg-base-200'
      }`}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      type='button'
      {...STagNew(stagSubject, stagAction)}
    >
      {recommended && (
        <span
          className='bg-primary/15 absolute right-12 top-12 rounded-4 px-8 py-2 text-xs font-medium text-primary'
          aria-hidden='true'
        >
          Recommended
        </span>
      )}
      {icon !== undefined && (
        <span
          className='flex h-40 w-40 items-center justify-center rounded-8 bg-base-200 text-primary'
          aria-hidden='true'
        >
          {icon}
        </span>
      )}
      <span className='text-md font-medium text-white'>{title}</span>
      <span className='text-sm text-secondary'>{description}</span>
    </button>
  );
};

// SVG icons matching the prototype's method card illustrations.
// Inline to avoid adding a new icon dependency for two one-off glyphs.
const NetworkScanIcon = (): ReactElement => (
  <svg viewBox='0 0 24 24' fill='none' className='h-20 w-20' aria-hidden='true'>
    <path
      d='M12 4a8 8 0 0 0-8 8m8-8a8 8 0 0 1 8 8m-8-8v3M4 12a8 8 0 0 0 8 8m-8-8h3m13 0a8 8 0 0 1-8 8m8-8h-3m-5 8v-3'
      stroke='currentColor'
      strokeWidth='1.6'
      strokeLinecap='round'
    />
    <circle cx='12' cy='12' r='2' fill='currentColor' />
  </svg>
);

const KeyboardIcon = (): ReactElement => (
  <svg viewBox='0 0 24 24' fill='none' className='h-20 w-20' aria-hidden='true'>
    <rect x='3' y='6' width='18' height='12' rx='2' stroke='currentColor' strokeWidth='1.6' />
    <path
      d='M7 10h.01M11 10h.01M15 10h.01M7 14h10'
      stroke='currentColor'
      strokeWidth='1.8'
      strokeLinecap='round'
    />
  </svg>
);
